// AI tools on a finished transcript: run each one and use its latest result.
// Times in results seek the episode player.

import { useEffect, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AI_TOOLS, clock, useAiResults, useRunTool, type AiResult, type AiTool } from '../../lib/transcripts';
import { jobError } from '../../lib/jobs';
import { errorInfo } from '../../lib/api';
import { useUpdateEpisode, type ShowEpisode } from '../../lib/shows';
import { Button } from '../ui/Button';
import { ErrorMessage, Loading } from '../ui/Feedback';
import { useToast } from '../ui/Toast';
import { CopyButton } from '../shows/bits';
import { usePlayer } from './Player';

export function AiTools({ transcriptionId, episode }: { transcriptionId: string; episode: ShowEpisode }) {
  const results = useAiResults(transcriptionId);
  const run = useRunTool(transcriptionId, episode.id);
  const toast = useToast();
  const [open, setOpen] = useState<string>(AI_TOOLS[0]!.type);
  const qc = useQueryClient();

  // Show notes and chapters are written onto the episode by the worker.
  const saved = ['show-notes', 'chapters']
    .map((t) => results.data?.[t])
    .map((r) => (r?.status === 'completed' ? r.updatedAt : ''))
    .join('|');
  useEffect(() => {
    if (saved.replace(/\|/g, '')) void qc.invalidateQueries({ queryKey: ['episode', episode.id] });
  }, [saved, qc, episode.id]);

  const start = (tool: AiTool) =>
    run.mutate(tool, {
      onSuccess: () => setOpen(tool.type),
      onError: (err) => toast.show(errorInfo(err).message, 'error'),
    });

  const runAll = () => {
    for (const tool of AI_TOOLS) if (!results.data?.[tool.type]) run.mutate(tool);
  };
  const missing = AI_TOOLS.filter((t) => !results.data?.[t.type]).length;

  return (
    <section aria-labelledby="ai-heading" className="space-y-4 rounded-xl border border-border p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="ai-heading" className="text-lg font-semibold">
          AI tools
        </h2>
        {results.data && missing > 0 && (
          <Button size="sm" variant="secondary" onClick={runAll} loading={run.isPending}>
            Run {missing === AI_TOOLS.length ? 'all' : `the other ${missing}`}
          </Button>
        )}
      </div>
      {results.isLoading && <Loading />}
      {results.isError && <ErrorMessage error={results.error} onRetry={() => results.refetch()} />}
      {results.data && (
        <div className="grid gap-4 md:grid-cols-[12rem_minmax(0,1fr)]">
          <ul
            role="tablist"
            aria-label="AI tools"
            aria-orientation="vertical"
            className="flex gap-1 overflow-x-auto md:flex-col"
          >
            {AI_TOOLS.map((tool) => {
              const r = results.data[tool.type];
              return (
                <li key={tool.type}>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={open === tool.type}
                    onClick={() => setOpen(tool.type)}
                    className={`flex w-full items-center justify-between gap-2 whitespace-nowrap rounded-md px-3 py-2 text-left text-sm ${open === tool.type ? 'bg-accent-soft font-medium text-accent' : 'text-text hover:bg-surface'}`}
                  >
                    {tool.label}
                    <StatusDot result={r} />
                  </button>
                </li>
              );
            })}
          </ul>
          {AI_TOOLS.filter((t) => t.type === open).map((tool) => (
            <div key={tool.type} role="tabpanel" aria-label={tool.label} className="min-w-0 space-y-3">
              <ToolPanel
                tool={tool}
                result={results.data[tool.type]}
                episode={episode}
                onRun={() => start(tool)}
                running={run.isPending && run.variables?.type === tool.type}
              />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function StatusDot({ result }: { result: AiResult | undefined }) {
  if (!result) return null;
  const busy = result.status === 'pending' || result.status === 'active';
  const [cls, label] = busy
    ? ['bg-accent animate-pulse', 'Running']
    : result.status === 'failed'
      ? ['bg-danger', 'Failed']
      : ['bg-success', 'Ready'];
  return <span aria-label={label} title={label} className={`size-2 shrink-0 rounded-full ${cls}`} />;
}

function ToolPanel({
  tool,
  result,
  episode,
  onRun,
  running,
}: {
  tool: AiTool;
  result: AiResult | undefined;
  episode: ShowEpisode;
  onRun: () => void;
  running: boolean;
}) {
  const busy = result?.status === 'pending' || result?.status === 'active';
  const header = (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="text-sm text-muted">{tool.blurb}</p>
      <Button size="sm" variant={result ? 'secondary' : 'primary'} onClick={onRun} loading={running || busy}>
        {result ? 'Regenerate' : 'Generate'}
      </Button>
    </div>
  );
  if (!result) return header;
  if (busy)
    return (
      <>
        {header}
        <p role="status" className="text-sm text-muted">
          Working on it…
        </p>
      </>
    );
  if (result.status === 'failed')
    return (
      <>
        {header}
        <p className="text-sm text-danger" title={result.errorMessage ?? undefined}>
          {jobError(result.errorMessage)}
        </p>
      </>
    );
  return (
    <>
      {header}
      <Output type={tool.type} data={result.outputData ?? {}} episode={episode} />
    </>
  );
}

const list = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

function Output({ type, data, episode }: { type: string; data: Record<string, unknown>; episode: ShowEpisode }) {
  switch (type) {
    case 'show-notes':
      return <ShowNotes notes={typeof data.showNotes === 'string' ? data.showNotes : ''} />;
    case 'chapters':
      return (
        <TimedList
          items={list<{ title: string; startMs: number }>(data.chapters).map((c) => ({ at: c.startMs, text: c.title }))}
          copy={(items) => items.map((c) => `${clock(c.at ?? 0)} ${c.text}`).join('\n')}
          note="Saved on the episode."
        />
      );
    case 'title-suggest':
      return <Titles titles={list<string>(data.titles)} episode={episode} />;
    case 'social-posts':
      return (
        <ul className="space-y-3">
          {list<{ platform: string; text: string }>(data.posts).map((p) => (
            <Card key={p.platform} title={PLATFORMS[p.platform] ?? p.platform} copy={p.text}>
              <p className="whitespace-pre-wrap text-sm">{p.text}</p>
            </Card>
          ))}
        </ul>
      );
    case 'highlight-detect':
      return (
        <ul className="space-y-3">
          {list<{
            title: string;
            reason: string;
            startMs: number;
            endMs: number;
          }>(data.highlights).map((h) => (
            <Card key={`${h.startMs}-${h.title}`} title={h.title} at={h.startMs} end={h.endMs}>
              <p className="text-sm text-muted">{h.reason}</p>
            </Card>
          ))}
        </ul>
      );
    case 'hooks':
      return (
        <TimedList
          items={list<{ text: string; sourceStartMs?: number }>(data.hooks).map((h) => ({
            at: h.sourceStartMs,
            text: h.text,
          }))}
          copy={(items) => items.map((h) => h.text).join('\n')}
        />
      );
    default:
      return null;
  }
}

const PLATFORMS: Record<string, string> = {
  twitter: 'X / Twitter',
  linkedin: 'LinkedIn',
  instagram: 'Instagram',
  facebook: 'Facebook',
  threads: 'Threads',
  tiktok: 'TikTok',
  youtube: 'YouTube',
};

function Seek({ at, end }: { at: number; end?: number }) {
  const player = usePlayer();
  return (
    <button
      type="button"
      onClick={() => player.seek(at)}
      aria-label={`Play from ${clock(at)}`}
      className="shrink-0 rounded bg-surface px-1.5 py-0.5 text-xs tabular-nums text-accent hover:bg-accent-soft"
    >
      ▶ {clock(at)}
      {end !== undefined ? `–${clock(end)}` : ''}
    </button>
  );
}

function Card({
  title,
  copy,
  at,
  end,
  children,
}: {
  title: string;
  copy?: string;
  at?: number;
  end?: number;
  children: ReactNode;
}) {
  return (
    <li className="space-y-1.5 rounded-lg border border-border p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">{title}</span>
        <span className="flex items-center gap-1">
          {at !== undefined && <Seek at={at} end={end} />}
          {copy && <CopyButton value={copy} />}
        </span>
      </div>
      {children}
    </li>
  );
}

function TimedList({
  items,
  copy,
  note,
}: {
  items: Array<{ at?: number; text: string }>;
  copy: (items: Array<{ at?: number; text: string }>) => string;
  note?: string;
}) {
  return (
    <div className="space-y-2">
      <ul className="divide-y divide-border rounded-lg border border-border">
        {items.map((it, i) => (
          <li key={i} className="flex items-center gap-3 px-3 py-2 text-sm">
            {it.at !== undefined ? <Seek at={it.at} /> : <span className="w-14 shrink-0" aria-hidden />}
            <span className="min-w-0 flex-1">{it.text}</span>
          </li>
        ))}
      </ul>
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted">{note}</span>
        <CopyButton value={copy(items)} label="Copy all" />
      </div>
    </div>
  );
}

function ShowNotes({ notes }: { notes: string }) {
  return (
    <div className="space-y-2">
      <div className="max-h-80 overflow-y-auto whitespace-pre-wrap rounded-lg border border-border bg-surface p-3 text-sm">
        {notes}
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted">Saved as the episode’s show notes.</span>
        <CopyButton value={notes} />
      </div>
    </div>
  );
}

function Titles({ titles, episode }: { titles: string[]; episode: ShowEpisode }) {
  const update = useUpdateEpisode(episode);
  const toast = useToast();
  return (
    <ul className="divide-y divide-border rounded-lg border border-border">
      {titles.map((t) => (
        <li key={t} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
          <span>{t}</span>
          {t === episode.title ? (
            <span className="text-xs text-success">Current title</span>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              loading={update.isPending && update.variables?.title === t}
              onClick={() =>
                update.mutate(
                  { title: t },
                  {
                    onSuccess: () => toast.show('Title updated', 'success'),
                    onError: (err) => toast.show(errorInfo(err).message, 'error'),
                  },
                )
              }
            >
              Use
            </Button>
          )}
        </li>
      ))}
    </ul>
  );
}
